import VisibilityIcon from "@mui/icons-material/Visibility";
import { Button, IconButton, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import React, { useEffect, useRef, useState } from "react";
import SideNav from "component/layouts/sideNav";
import {
  ADD,
  DELETE,
  GO,
  MATERIAL_MANAGEMENT,
  MATERIAL_TRANSFER,
} from "resources/constant/strings/string";

import {
  employeeCellRenderer,
  setEmployees,
  setItemCategories,
  setItemList,
  setItems,
  setSiteCategories,
  setSites,
  setSuppliers,
  setUoms,
  setUsers,
  siteCellRenderer,
  userCellRenderer,
} from "component/cellRenderers/renderers";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";

import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { APILOGIN } from "apihandler/login";
import { APIMASTER } from "apihandler/master";
import { APIMATERIALMGMT } from "apihandler/materialmgmt";
import {
  setAlertConfirmDeletion,
  setAlertTitleText,
} from "component/modal/alertModal";
import {
  PATH_LOGIN,
  PATH_MATERIAL_TRANSFER,
  PATH_MATERIAL_TRANSFER_DETAIL,
} from "resources/constant/url/url";

export default function MaterialTransfer() {
  const { date } = useParams();
  const location = useLocation();

  const [dateValue, setDateValue] = React.useState(null);
  const [toDateValue, setToDateValue] = React.useState(dayjs().endOf("month"));

  const navigate = useNavigate();

  const gridRef = useRef();
  const gridApiRef = useRef(null);
  const gridColumnApiRef = useRef(null);

  // ---- persistence keys ----
  const STORAGE_KEY = "mt_list_grid_state"; // sort+page (general)
  const RETURN_FILTER_KEY = "mt_return_filters"; // filters to re-apply only when coming back from detail

  // ---------- helpers ----------
  const loadGridState = () => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  // Save ONLY sort + page (not filters). Filters are handled separately.
  const saveGridState = () => {
    if (!gridApiRef.current || !gridColumnApiRef.current) return;
    const colState = gridColumnApiRef.current.getColumnState();
    const sortModel = (colState || []).filter((c) => !!c.sort);
    const page =
      gridApiRef.current.paginationGetCurrentPage &&
      gridApiRef.current.paginationGetCurrentPage();

    const payload = {
      sortModel,
      page: typeof page === "number" ? page : 0,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  };

  // Apply ONLY sort + page
  const applyGridState = (state) => {
    if (!state || !gridApiRef.current || !gridColumnApiRef.current) return;

    if (state.sortModel?.length) {
      gridColumnApiRef.current.applyColumnState({
        state: state.sortModel,
        applyOrder: true,
      });
    }

    if (
      typeof state.page === "number" &&
      gridApiRef.current.paginationGoToPage
    ) {
      gridApiRef.current.paginationGoToPage(state.page);
    }

    gridApiRef.current.onFilterChanged();
  };

  // Clear all filters (column + quick)
  const clearAllFilters = () => {
    if (!gridApiRef.current) return;
    gridApiRef.current.setFilterModel(null);
    gridApiRef.current.setQuickFilter && gridApiRef.current.setQuickFilter("");
    gridApiRef.current.onFilterChanged();
  };

  // Save current filters so they can be restored when returning from detail
  const saveReturnFilters = () => {
    if (!gridApiRef.current) return;
    const filterModel = gridApiRef.current.getFilterModel();
    const payload = {
      filterModel,
      from: (dateValue || dayjs().startOf("month")).format("YYYY-MM-DD"),
      to: (toDateValue || dayjs().endOf("month")).format("YYYY-MM-DD"),
    };
    try {
      sessionStorage.setItem(RETURN_FILTER_KEY, JSON.stringify(payload));
    } catch {}
  };

  // Load & clear once (one-time use)
  const loadReturnFiltersOnce = () => {
    try {
      const raw = sessionStorage.getItem(RETURN_FILTER_KEY);
      if (!raw) return null;
      sessionStorage.removeItem(RETURN_FILTER_KEY);
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  // Persist on unload as a fallback
  useEffect(() => {
    const handler = () => saveGridState();
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, [navigate]);

  const [selectedRowId, setSelectedRowId] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const deleteModel = () => {
    if (selectedRowIds.length === 0) {
      return;
    }
    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      APIMATERIALMGMT.DeleteMaterialTransfers(selectedRowId)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current.api.getSelectedRows();
            gridRef.current.api.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");
            saveGridState();
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
          }
        });
    });
  };

  const updateModel = (model) => {
    gridRef.current.api.applyTransaction({ update: [model] });
    saveGridState();
  };

  const addModel = (model) => {
    gridRef.current.api.applyTransaction({ add: [model], addIndex: 0 });
    saveGridState();
  };

  const onRowSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    if (selectedRows.length > 0) {
      setSelectedRowId(selectedRows[0].id);
      const ids = selectedRows.map((row) => row.id);
      setSelectedRowIds(ids);
    } else {
      setSelectedRowId(0);
      setSelectedRowIds([]);
    }
  };

  const columnDefs = [
    {
      flex: 1.5,
      headerName: "Transaction No",
      field: "transactionNo",
      checkboxSelection: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
      wrapText: true,
      autoHeight: true,
    },
    {
      headerName: "Transaction Date",
      field: "transactionDate",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
      wrapText: true,
      autoHeight: true,
    },
    {
      flex: 1.5,
      headerName: "From Site",
      field: "fromSite",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) =>
        siteCellRenderer({ value: params.data.fromSite }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      flex: 1.5,
      headerName: "To Site",
      field: "toSite",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) => siteCellRenderer({ value: params.data.toSite }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      headerName: "Reported By",
      field: "reportedBy",
      filter: false,
      floatingFilter: false,
      valueGetter: (params) =>
        employeeCellRenderer({ value: params.data.reportedBy }),
      cellStyle: { "border-right-color": "#e2e2e2" },
      wrapText: true,
      autoHeight: true,
    },
    {
      headerName: "Modified By",
      field: "modifiedBy",
      filter: false,
      floatingFilter: false,
      valueGetter: (params) =>
        userCellRenderer({ value: params.data.modifiedBy }),
      cellStyle: { "border-right-color": "#e2e2e2" },
      wrapText: true,
      autoHeight: true,
    },
    {
      headerName: "Modified Date",
      field: "modifiedDate",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
      wrapText: true,
      autoHeight: true,
    },
    {
      flex: 0.7,
      headerName: "Action",
      field: "id",
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            aria-label="edit"
            onClick={() => {
              // 1) save sort/page
              saveGridState();
              // 2) save current filters so coming back restores the same search
              saveReturnFilters();
              // 3) navigate
              navigate(PATH_MATERIAL_TRANSFER_DETAIL + params.data.id, {
                state: {
                  date: (dateValue || dayjs()).format("YYYY-MM-DD"),
                  isEdit: false,
                  fromList: true,
                },
              });
            }}
          >
            <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
    flex: 1,
    filter: "agTextColumnFilter",
    filterParams: {
      caseSensitive: false,
      defaultOption: "startsWith",
    },
    floatingFilter: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  };

  const fetchMaterialTransfers = (d1, d2) => {
    return APIMATERIALMGMT.MaterialTransfers(
      d1.format("YYYY-MM-DD"),
      d2.format("YYYY-MM-DD")
    )
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) {
          setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
          return [];
        }
        const data = resp.response.sort((a, b) => b.id - a.id);
        return Promise.resolve(data);
      });
  };

  const onGridReady = (params) => {
    gridApiRef.current = params.api;
    gridColumnApiRef.current = params.columnApi;

    const maybeReturn = loadReturnFiltersOnce();
    if (maybeReturn?.from && maybeReturn?.to) {
      const from = dayjs(maybeReturn.from);
      const to = dayjs(maybeReturn.to);
      setDateValue(from);
      setToDateValue(to);
    } else {
      if (date) {
        setDateValue(dayjs(date));
      } else {
        setDateValue(dayjs().startOf("month"));
      }
      setToDateValue((prev) => prev || dayjs().endOf("month"));
    }

    // preload masters
    APIMASTER.SupplierOptions()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode === 200) setSuppliers(resp.response);
      });
    APIMASTER.UomOptions()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode === 200) setUoms(resp.response);
      });
    APIMASTER.UserOptions()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode === 200) setUsers(resp.response);
      });
    APIMASTER.EmployeeOptions()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode === 200) setEmployees(resp.response);
      });
    APIMASTER.ItemCategories()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode === 200) setItemCategories(resp.response);
      });
    APIMASTER.SiteCategories()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode === 200) setSiteCategories(resp.response);
      });
    APIMASTER.SiteOptions()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode === 200) setSites(resp.response);
      });
    APIMASTER.Items()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        const itemOptions = resp.response.map((el) => ({
          name: el.itemName,
          value: el.id,
          dependentvalue: el.categoryId,
        }));
        setItems(itemOptions);
        setItemList(resp.response);
      });

    // fetch rows
    const addRows = (d1, d2) =>
      fetchMaterialTransfers(d1, d2).then((data) => {
        params.api.applyTransaction({ add: data });

        // If we’re returning from detail, re-apply the last filters.
        // Otherwise (fresh load), clear filters and only apply sort/page.
        if (maybeReturn?.filterModel) {
          gridApiRef.current.setFilterModel(maybeReturn.filterModel);
          gridApiRef.current.onFilterChanged();
        } else {
          clearAllFilters(); // show full data on true refresh
        }

        // re-apply only sort + page
        const s = loadGridState();
        if (s) {
          setTimeout(() => {
            applyGridState(s);
          }, 0);
        }
      });

    // Use restored date range if available; else fallbacks
    const fromForFetch =
      (maybeReturn?.from && dayjs(maybeReturn.from)) ||
      (date ? dayjs(date) : dayjs().startOf("month"));
    const toForFetch =
      (maybeReturn?.to && dayjs(maybeReturn.to)) ||
      dayjs(toDateValue || dayjs().endOf("month"));

    addRows(fromForFetch, toForFetch);
  };

  return (
    <SideNav
      currentComponent={MATERIAL_MANAGEMENT}
      subComponent={MATERIAL_TRANSFER}
      subComponentPath={PATH_MATERIAL_TRANSFER}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            navigate(PATH_MATERIAL_TRANSFER_DETAIL + 0, {
              state: {
                date: (dateValue || dayjs()).format("YYYY-MM-DD"),
                isEdit: false,
              },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={deleteModel}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>

      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              value={dateValue}
              onChange={(newValue) => setDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={toDateValue}
              onChange={(newValue) => setToDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div align="right" style={{ width: "100%", right: 0 }}>
          <Button
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
            }}
            onClick={() => {
              fetchMaterialTransfers(dateValue, toDateValue).then((data) => {
                try {
                  const rowData = [];
                  gridRef.current.api.forEachNode((node) =>
                    rowData.push(node.data)
                  );
                  gridRef.current.api.applyTransaction({ remove: rowData });
                } catch (e) {}

                // GO is a "fresh query" → clear filters
                clearAllFilters();
                // also drop any return filters
                sessionStorage.removeItem(RETURN_FILTER_KEY);

                try {
                  gridRef.current.api.applyTransaction({ add: data });
                  const s = loadGridState();
                  if (s) setTimeout(() => applyGridState(s), 0);
                } catch (e) {}
              });
            }}
          >
            {GO}
          </Button>
        </div>
      </div>

      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard
            enableRangeSelection
            columnDefs={columnDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            paginationAutoPageSize
            pagination
            rowSelection="multiple"
            getRowId={(params) => params.data.id}
            onSelectionChanged={onRowSelectionChanged}
            // Save on every user change (sort/page only)
            onFilterChanged={() => saveGridState()}
            onSortChanged={() => saveGridState()}
            onPaginationChanged={() => saveGridState()}
            onFirstDataRendered={() => {
               const returnFilters = loadReturnFiltersOnce();
              if (returnFilters?.filterModel) {
                gridApiRef.current?.setFilterModel(returnFilters.filterModel);
                gridApiRef.current?.onFilterChanged();
              } else {
              const saved = loadGridState();
              if (saved) setTimeout(() => applyGridState(saved), 0);
              }
            }}
            suppressBrowserResizeObserver={true}
          />
        </div>
      </div>
    </SideNav>
  );
}
